import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { RefreshTokenResponse } from '@core/models/application.models';
import { MasterDataService } from '@core/services/master-data.service';
import { PosService } from '@core/services/pos.service';
import { environment } from '@env/environment';

import { BASE_URL } from './base-url.token';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private router = inject(Router);
  private http = inject(HttpClient);
  private cookie = inject(CookieService);
  private baseUrl = inject(BASE_URL);
  private masterService = inject(MasterDataService);
  private posService = inject(PosService);

  posApiBaseUrl: string = environment.posApiBaseUrl;
  companyApiBaseUrl: string = environment.companyApiBaseUrl;
  companyWebBaseUrl: string = environment.companyWebBaseUrl;
  productionSystemApiBaseUrl: string = environment.productionSystemApiBaseUrl;
  private isRefreshing = false;
  /** The token the running refresh produced; null while it is still running. */
  private refreshedToken$ = new BehaviorSubject<string | null>(null);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (
      request.url.startsWith(this.baseUrl) ||
      request.url.startsWith(this.posApiBaseUrl) ||
      request.url.startsWith(this.productionSystemApiBaseUrl) ||
      request.url.startsWith(this.companyApiBaseUrl)
    ) {
      const token = this.masterService.getToken();
      if (token != null) {
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        });
      }
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && request.url.startsWith(this.baseUrl)) {
          return this.handle401Error(request, next);
        }

        if (error.status === 403 && request.url.startsWith(this.baseUrl)) {
          this.router.navigate(['/production/home/departments']);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Every request that fails while the token is refreshed waits for the new token and is sent again, so a screen
    // that loads several things at once does not show errors when the session is renewed.
    if (!this.isRefreshing) {
      const refreshToken = this.cookie.get('refreshToken');
      if (!refreshToken) {
        this.posService.logout();
        return throwError(() => new Error('Refresh token is not available'));
      }
      this.startRefresh(refreshToken);
    }

    return this.refreshedToken$.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap(token => next.handle(this.withToken(req, token)))
    );
  }

  /**
   * Refreshes the token in its own subscription, not inside the request that hit the 401: if that screen is closed
   * while the refresh runs, the refresh still finishes and the other waiting requests do not hang.
   */
  private startRefresh(refreshToken: string): void {
    this.isRefreshing = true;
    this.refreshedToken$.next(null);
    const refreshUrl = `${environment.companyApiBaseUrl}/auth/refresh`;
    this.http.post<RefreshTokenResponse>(refreshUrl, { refreshToken }).subscribe({
      next: response => {
        this.isRefreshing = false;
        const domain = `${environment.domainName}`;
        document.cookie = `accessToken=${response.accessToken}; Path=/; Secure; SameSite=None; Domain=${domain}`;
        this.refreshedToken$.next(response.accessToken);
      },
      error: err => {
        this.isRefreshing = false;
        // Fail the requests that were waiting for this refresh, so their screens stop loading instead of hanging.
        const waiting = this.refreshedToken$;
        this.refreshedToken$ = new BehaviorSubject<string | null>(null);
        waiting.error(err);
        this.posService.logout();
      }
    });
  }

  private withToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
